import { Bitmap, Color, GlowFilter, MovieClip, Point } from "../runtime";
import {
  PlayerAction,
  PlayerDeathAction,
  PlayerGuiAction,
  PlayerSound,
} from "./actions";
import type { PlayerDeathUpdate, PlayerUpdate } from "./actions";
import {
  EFF_playerHit,
  EFF_strikeX,
  SPR_knight,
  SPR_knightArmoured,
  SPR_sword,
  SPR_swordBack,
  SPR_swordTrail,
  SPR_swordTrailBack,
} from "./assets";
import { blockMovementAxes, type EnemyHitData } from "./combat";
import { KNIGHT_FRAMES, KNIGHT_SWORD_FRAMES } from "./data/knightFrames";
import { SWORD_COLOURS } from "./data/swordColours";
import { WEAPONS } from "./data/weapons";
import type { Obstacle, TriggerPass } from "./level";
import { SpriteLayer, SwordGlowPulse } from "./SpriteLayer";

/** Weapon sheet cell edge, in pixels. The body sheet uses the same grid. */
const TILE_WEAPON = 64;

/** Target angle and 8-way facing per input pair, indexed `(yAxis + 1) * 3 + xAxis + 1`; null holds the facing. */
const MOVE_DIRS = [
  { angle: 225, dir8: 7 },
  { angle: 270, dir8: 0 },
  { angle: 315, dir8: 1 },
  { angle: 180, dir8: 6 },
  null,
  { angle: 0, dir8: 2 },
  { angle: 135, dir8: 5 },
  { angle: 90, dir8: 4 },
  { angle: 45, dir8: 3 },
] as const;

/** Idle pose per facing. `flipped: null` keeps whichever way the sprite already faces. */
const FACINGS = [
  { animOffset: 20, flipped: null, swordInFront: true },
  { animOffset: 0, flipped: null, swordInFront: false },
  { animOffset: 10, flipped: true, swordInFront: false },
  { animOffset: 10, flipped: false, swordInFront: false },
] as const;

/** Dash pose per 8-way facing. */
const DASH_POSES = [
  { animOffset: 73, flipped: null },
  { animOffset: 74, flipped: false },
  { animOffset: 75, flipped: false },
  { animOffset: 76, flipped: false },
  { animOffset: 77, flipped: null },
  { animOffset: 76, flipped: true },
  { animOffset: 75, flipped: true },
  { animOffset: 74, flipped: true },
] as const;

/** Slash pose per facing; facings 2 and 3 differ only in the flip. */
const SLASH_POSES = [
  {
    speed: 70,
    animOffset: 42,
    angle: -5,
    offX: 25,
    offY: -10,
    xTarget: -4.5,
    yTarget: -2.5,
    flipped: null,
  },
  {
    speed: 70,
    animOffset: 30,
    angle: 175,
    offX: -23,
    offY: -7,
    xTarget: 8.5,
    yTarget: -2.5,
    flipped: null,
  },
  {
    speed: 80,
    animOffset: 36,
    angle: 280,
    offX: -21,
    offY: -4,
    xTarget: -7,
    yTarget: 2.5,
    flipped: true,
  },
  {
    speed: 80,
    animOffset: 36,
    angle: 280,
    offX: -21,
    offY: -4,
    xTarget: -7,
    yTarget: 2.5,
    flipped: false,
  },
] as const;

/** Sign the enemy's offset must carry for a directional slash to land, by `dir8`; 0 ignores that axis. */
const SLASH_ARC_X: readonly number[] = [0, 1, 1, 1, 0, -1, -1, -1];
const SLASH_ARC_Y: readonly number[] = [-1, -1, 0, 1, 1, 1, 0, -1];

/** Body flash tint by `colourMaskType`: hurt, healed, powered up. */
const COLOUR_MASKS: readonly number[] = [16711770, 851712, 16777215];

/** Energy bled off per frame at each sword level. */
const DRAIN_RATE: readonly number[] = [-1, -2, -3, -3];

/** Full xenos bar: sword level 3 and the longest blade reach. */
const MAX_ENERGY = 3000;

interface HitSparkState {
  counter: number;
  alternator: boolean;
  size: number;
  alphaCurrent: number;
  sizeMult: number;
}

export class Knight extends MovieClip {
  weapon = 0;
  shotDir = 0;
  hasWeapon = true;
  blackHoleOn = false;
  spinOn = false;
  hitCD = 0;
  animState = -1;
  invincible = false;
  control = false;
  targetAngle = 0;
  moving = false;
  hpCurrent = 100;
  stuck = false;
  slashing = false;
  energy = 0;
  swordLevel = 0;
  dead = false;
  freeze = 0;
  powerXOn = false;
  /**
   * God mode. Blocks all incoming damage, pins energy at maximum, and drops
   * the cost, wind-up and one-at-a-time locks from every special attack.
   */
  cheats = false;
  private readonly body: SpriteLayer;
  private readonly swordLayer: SpriteLayer;
  private readonly swordBackLayer: SpriteLayer;
  private readonly trailLayer: SpriteLayer;
  private readonly trailBackLayer: SpriteLayer;
  private readonly canvasBitmap: Bitmap;
  private readonly powerX: MovieClip;
  private readonly sword: MovieClip;
  private readonly swordBack: MovieClip;
  private readonly swordTrailMain: MovieClip;
  private readonly swordTrailBack: MovieClip;
  private readonly swordTrail: MovieClip[] = [];
  private readonly swordTrailData: number[] = [];
  private readonly triggerPass: TriggerPass[];
  private readonly weaponData: number[];
  private readonly hyperArmour: boolean;
  private readonly levelEdgeX: number;
  private readonly levelEdgeY: number;
  private readonly canDie: boolean;
  private readonly topSpeed = 11;
  private readonly hitBoxSize = 8;
  private readonly pushForce = new Point(0, 0);
  private readonly isPressed: boolean[] = [false, false, false, false];
  private trailWide = false;
  private xSpeed = 0;
  private ySpeed = 0;
  private powerXCounter = 0;
  private powerXAlpha = 0;
  private powerXScale = 0;
  private powerXFading = false;
  private swordTrailFrame = 0;
  private swordTrailBackFrame = 0;
  private swordFadeSpeed = 10;
  private restartTrail = false;
  private changeWeapon = 0;
  private action = PlayerAction.None;
  private soundAction = PlayerSound.None;
  private guiAction = PlayerGuiAction.None;
  private readonly playerUpdate: PlayerUpdate = {
    xAxis: 0,
    yAxis: 0,
    action: PlayerAction.None,
    sound: PlayerSound.None,
    guiAction: PlayerGuiAction.None,
  };
  private readonly playerDeathUpdate: PlayerDeathUpdate = {
    action: PlayerDeathAction.None,
  };
  private spacePressed = false;
  private dashCounter = 0;
  private spinCounter = 0;
  private spinSpeed = 0;
  private frameCounter = 0;
  private frame = 0;
  private frameLimit = 0;
  private animOffset = 0;
  private moveSpeed = 11;
  private xTarget: number;
  private yTarget: number;
  private flipped = false;
  private dir = 1;
  private dir8 = 5;
  private numPressed = false;
  private readonly moveAllowedPrevious: [boolean, boolean] = [false, false];
  private readonly moveAllowedCurrent: [boolean, boolean] = [true, true];
  private hasBack = false;
  private readonly glowPulse = new SwordGlowPulse();
  private brightnessSword = 0;
  private colourMaskCD = 0;
  private colourMaskType = 0;
  private slashCounter = 0;
  private slashAngle = 0;
  private slashSpeed = 0;
  private slashOffX = 0;
  private slashOffY = 0;
  private slashXTarget = 0;
  private slashYTarget = 0;
  private swordRadius = 29;
  private swordSizeX = 0.75;
  private swordSizeY = 0.75;
  private deathCounter = 0;
  private hitSpark: MovieClip | null = null;
  private hitSparkData!: HitSparkState;

  constructor(
    levelX: number,
    levelY: number,
    x: number,
    y: number,
    triggerPass: TriggerPass[],
    weapon: number,
    canDie: boolean,
    weaponData: number[],
    hyperArmour: boolean,
  ) {
    super();
    this.hyperArmour = hyperArmour;
    this.triggerPass = triggerPass;
    this.weapon = weapon;
    this.canDie = canDie;
    this.weaponData = weaponData;
    this.powerX = new MovieClip();
    this.powerX.y = -7;
    this.addChild(this.powerX);
    const pXSpr = new Bitmap(new EFF_strikeX());
    pXSpr.x = pXSpr.width * -0.5;
    pXSpr.y = pXSpr.height * -0.5;
    pXSpr.smoothing = true;
    this.powerX.addChild(pXSpr);
    this.powerX.visible = false;
    if (this.weapon === 1 || this.weapon === 3) {
      this.hasBack = true;
    }
    this.levelEdgeX = levelX;
    this.levelEdgeY = levelY;
    this.trailLayer = new SpriteLayer(new SPR_swordTrail(), TILE_WEAPON);
    this.trailBackLayer = new SpriteLayer(
      new SPR_swordTrailBack(),
      TILE_WEAPON,
    );
    this.swordTrailMain = new MovieClip();
    this.addChild(this.swordTrailMain);
    this.swordTrailBack = new MovieClip();
    this.addChild(this.swordTrailBack);
    for (let trailIndex = 0; trailIndex < 10; trailIndex++) {
      const swordTrailHolder = new MovieClip();
      let ghost: Bitmap;
      if (trailIndex < 5) {
        this.swordTrailMain.addChild(swordTrailHolder);
        ghost = new Bitmap(this.trailLayer.canvas);
      } else {
        this.swordTrailBack.addChild(swordTrailHolder);
        ghost = new Bitmap(this.trailBackLayer.canvas);
      }
      ghost.x -= ghost.width * 0.03;
      ghost.y -= ghost.height * 0.5;
      swordTrailHolder.addChild(ghost);
      swordTrailHolder.scaleX = 0.75;
      swordTrailHolder.scaleY = 0.75;
      this.swordTrailData.push(trailIndex * 10);
      this.swordTrail.push(swordTrailHolder);
    }
    this.body = new SpriteLayer(
      this.hyperArmour ? new SPR_knightArmoured() : new SPR_knight(),
      TILE_WEAPON,
    );
    this.canvasBitmap = new Bitmap(this.body.canvas);
    this.addChild(this.canvasBitmap);
    this.canvasBitmap.x = TILE_WEAPON * -0.5;
    this.canvasBitmap.y = TILE_WEAPON * -0.5;
    this.sword = new MovieClip();
    this.sword.scaleX = 0.75;
    this.sword.scaleY = 0.75;
    this.addChild(this.sword);
    this.swordLayer = new SpriteLayer(new SPR_sword(), TILE_WEAPON);
    const swordBitmap = new Bitmap(this.swordLayer.canvas);
    this.sword.addChild(swordBitmap);
    swordBitmap.x = TILE_WEAPON * -0.03;
    swordBitmap.y = TILE_WEAPON * -0.5;
    swordBitmap.smoothing = true;
    this.swordBack = new MovieClip();
    this.swordBack.scaleX = 0.75;
    this.swordBack.scaleY = -0.75;
    this.addChild(this.swordBack);
    // back-blade canvas is 48px while its sheet cells are 64px — original quirk, the cell is clipped
    this.swordBackLayer = new SpriteLayer(new SPR_swordBack(), TILE_WEAPON, 48);
    const swordBackBitmap = new Bitmap(this.swordBackLayer.canvas);
    this.swordBack.addChild(swordBackBitmap);
    swordBackBitmap.x = TILE_WEAPON * -0.03;
    swordBackBitmap.y = TILE_WEAPON * -0.5;
    swordBackBitmap.smoothing = true;
    if (this.hasBack === false) {
      this.swordBack.visible = false;
      this.swordTrailBack.visible = false;
    }
    this.x = x;
    this.y = y;
    this.xTarget = x;
    this.yTarget = y;
    this.changeAnimState(0);
    this.drawTile(0);
    this.trailWide = false;
    this.setTrailFrame();
    this.drawSword();
    this.updateSword();
  }

  gainControl(): void {
    this.control = true;
  }

  slashHit(enemyData: EnemyHitData): number {
    if (enemyData.dead) {
      return 0;
    }
    const dx = enemyData.xx - this.x;
    const dy = enemyData.yy - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (this.animState === 4) {
      if (dist < 43 + this.swordLevel * 10) {
        return this.registerHit();
      }
      return 0;
    }
    if (dist >= this.swordRadius + enemyData.hitRadius) {
      return 0;
    }
    if (this.spinOn) {
      return this.registerHit();
    }
    const sideX = SLASH_ARC_X[this.dir8];
    const sideY = SLASH_ARC_Y[this.dir8];
    if (
      (sideX === 0 || Math.sign(dx) === sideX) &&
      (sideY === 0 || Math.sign(dy) === sideY)
    ) {
      return this.registerHit();
    }
    return 0;
  }

  gainHP(amount: number): void {
    this.hpCurrent += amount;
    if (this.hpCurrent > 100) {
      this.hpCurrent = 100;
    }
    this.flashBody(1, 125);
  }

  updateEnergy(amount: number = 0): void {
    const previousLevel = this.swordLevel;
    if (amount === 0) {
      // the original's per-level table is dead code: this formula overwrote it, so level 3 gains -30
      amount = 120 - this.swordLevel * 50;
    }
    this.energy += amount;
    if (this.energy > MAX_ENERGY) {
      this.energy = MAX_ENERGY;
    }
    if (this.energy < 0) {
      this.energy = 0;
    }
    // the level checks cascade on purpose: one big pickup can climb several levels in a single call
    if (amount > 0) {
      if (this.swordLevel === 0 && this.energy >= 1000) {
        this.swordLevel = 1;
      }
      if (this.swordLevel === 1 && this.energy >= 2000) {
        this.swordLevel = 2;
      }
      if (this.swordLevel === 2 && this.energy === MAX_ENERGY) {
        this.swordLevel = 3;
        this.maxPowerX(0);
      }
    }
    if (amount < 0 && this.animState < 2 && this.hasWeapon) {
      if (this.swordLevel === 3 && this.energy <= 2000) {
        this.swordLevel = 2;
        this.maxPowerX(2);
      }
      if (this.swordLevel === 2 && this.energy <= 1000) {
        this.swordLevel = 1;
      }
      if (this.swordLevel === 1 && this.energy === 0) {
        this.swordLevel = 0;
      }
    }
    if (this.swordLevel !== previousLevel) {
      this.drawSword();
      this.setTrailFrame();
    }
  }

  update(
    input: boolean[],
    obsArray: Obstacle[][],
    barrierArray: Obstacle[],
    draining: boolean,
  ): PlayerUpdate {
    if (this.animState === 4) {
      input[0] = false;
      input[1] = false;
      input[2] = false;
      input[3] = false;
    }
    ++this.slashCounter;
    if (this.slashCounter === 140) {
      this.slashCounter = 0;
    }
    if (this.freeze > 0) {
      --this.freeze;
    }
    if (this.animState !== 2) {
      this.updateAnim();
    } else {
      this.updateSpin();
    }
    this.updateGlow();
    let xAxis = 0;
    let yAxis = 0;
    if (input[0]) {
      yAxis--;
    }
    if (input[1]) {
      yAxis += 1;
    }
    if (input[2]) {
      xAxis--;
    }
    if (input[3]) {
      xAxis += 1;
    }
    const moveDir = MOVE_DIRS[(yAxis + 1) * 3 + xAxis + 1];
    if (moveDir != null) {
      this.targetAngle = moveDir.angle;
      this.dir8 = moveDir.dir8;
    }
    let speed = 0;
    if (xAxis !== 0 || yAxis !== 0) {
      speed = this.moveSpeed | 0;
      this.moving = true;
    } else {
      this.moving = false;
    }
    if (this.animState !== 4) {
      const rad = this.targetAngle * (Math.PI / 180);
      this.xSpeed = speed * Math.cos(rad);
      this.ySpeed = speed * Math.sin(rad);
    } else {
      ++this.dashCounter;
      if (this.dashCounter > 9) {
        this.xSpeed += (0 - this.xSpeed) * 0.35;
        this.ySpeed += (0 - this.ySpeed) * 0.35;
        if (this.dashCounter === 22) {
          this.action = PlayerAction.DashEnded;
        }
        if (this.dashCounter === 24) {
          this.invincible = false;
        }
        if (this.dashCounter === 38) {
          this.changeAnimState(0);
          this.dashCounter = 0;
        }
      }
    }
    if (this.animState !== 10) {
      this.xSpeed += this.pushForce.x;
      this.ySpeed += this.pushForce.y;
      this.xTarget += this.xSpeed;
      this.yTarget += this.ySpeed;
      this.pushForce.x += (0 - this.pushForce.x) * 0.1;
      this.pushForce.y += (0 - this.pushForce.y) * 0.1;
    }
    if (this.slashing) {
      this.moveSpeed = this.topSpeed * 0.45;
      this.updateTrail(5);
    }
    if (this.animState === 2) {
      this.moveSpeed = this.topSpeed * 0.45;
    }
    if (this.animState === 10) {
      this.moveSpeed = this.topSpeed * 0.1;
    }
    this.moveStep(obsArray, barrierArray);
    if (this.moveSpeed < this.topSpeed) {
      this.moveSpeed += 0.5;
      if (this.moveSpeed >= this.topSpeed) {
        this.moveSpeed = this.topSpeed;
      }
    }
    this.updateInput(0, yAxis === -1);
    this.updateInput(1, yAxis === 1);
    this.updateInput(2, xAxis === -1);
    this.updateInput(3, xAxis === 1);
    this.readSpecialKey(input);
    if (this.cheats) {
      // a positive delta is what cascades swordLevel up to 3 and fires maxPowerX
      if (this.energy < MAX_ENERGY) this.updateEnergy(MAX_ENERGY - this.energy);
    } else if (this.freeze === 0 && draining && this.energy > 0) {
      this.updateEnergy(DRAIN_RATE[this.swordLevel]);
    }
    this.readWeaponKeys(input);
    if (this.hitCD > 0) {
      --this.hitCD;
    }
    if (this.hitSpark != null) {
      this.updateHitSpark(0);
    }
    this.updateSlash();
    this.updateSword();
    if (this.hasWeapon) {
      this.updateTrail();
    }
    this.playerUpdate.xAxis = xAxis;
    this.playerUpdate.yAxis = yAxis;
    this.playerUpdate.action = this.action;
    this.playerUpdate.sound = this.soundAction;
    this.playerUpdate.guiAction = this.guiAction;
    if (this.action > 0) {
      this.action = PlayerAction.None;
    }
    if (this.soundAction > 0) {
      this.soundAction = PlayerSound.None;
    }
    // guiAction is never cleared: the original's reset sat behind an already-zeroed soundAction
    return this.playerUpdate;
  }

  updateDeath(): PlayerDeathUpdate {
    const returnAction = this.playerDeathUpdate;
    returnAction.action = PlayerDeathAction.None;
    ++this.deathCounter;
    if (this.deathCounter === 1) {
      returnAction.action = PlayerDeathAction.HideAndExplode;
    }
    if (this.deathCounter === 7) {
      returnAction.action = PlayerDeathAction.SpawnCrossStrikes;
    }
    if (this.deathCounter === 14) {
      returnAction.action = PlayerDeathAction.FinishDeath;
    }
    return returnAction;
  }

  changeAnimState(newState: number): void {
    if (newState === this.animState) {
      return;
    }
    this.invincible = false;
    this.restartTrail = true;
    this.animState = newState;
    switch (newState) {
      case 0:
        this.swordFadeSpeed = 50;
        this.frameLimit = 10;
        this.animOffset = 0;
        this.updateDir();
        break;
      case 1:
        this.soundAction = PlayerSound.Swipe;
        this.updateSlash(true);
        break;
      case 2:
        this.spinOn = true;
        this.spinCounter = 0;
        this.spinSpeed = 40;
        this.frameLimit = 18;
        this.animOffset = 55;
        this.swordFadeSpeed = 30;
        this.trailWide = true;
        this.setTrailFrame();
        this.invincible = true;
        break;
      case 3:
        this.updateSlash(true);
        // cheats throw copies of the blade, so the real one stays in hand
        if (!this.cheats) {
          this.sword.visible = false;
          this.swordBack.visible = false;
          this.swordTrailMain.visible = false;
          this.swordTrailBack.visible = false;
          this.hasWeapon = false;
        }
        this.soundAction = PlayerSound.StaffThrow;
        break;
      case 4: {
        const pose = DASH_POSES[this.dir8];
        this.animOffset = pose.animOffset;
        if (pose.flipped !== null) {
          this.flipped = pose.flipped;
        }
        this.invincible = true;
        const rad = this.targetAngle * (Math.PI / 180);
        this.xSpeed = 25 * Math.cos(rad);
        this.ySpeed = 25 * Math.sin(rad);
        this.frameLimit = 1;
        this.soundAction = PlayerSound.Dash;
        break;
      }
    }
    this.frame = 0;
    this.frameCounter = 0;
    switch (newState) {
      case 5:
        this.updateSlash(true);
        this.updateSlash(false);
        this.frameCounter = this.cheats ? 0 : -35;
        this.shotDir = this.targetAngle | 0;
        this.soundAction = PlayerSound.AxeCharge;
        break;
      case 10:
        this.animOffset = 48;
        this.frameLimit = 7;
        this.stackSprites(false);
        this.soundAction = PlayerSound.WeaponChange;
        break;
    }
  }

  shotCollision(
    x: number,
    y: number,
    shotRadius: number,
    damage: number,
  ): number {
    let hitType = 0;
    const dx = x - this.x;
    const dy = y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const hitAngle = Math.atan2(this.y - y, this.x - x) * (180 / Math.PI);
    if (this.invincible === false) {
      if (dist < 8 + shotRadius) {
        this.damage(damage, hitAngle);
        hitType = 2;
      }
    } else if (dist < this.swordRadius + shotRadius) {
      hitType = 1;
    }
    return hitType;
  }

  damage(amount: number = 1, dAngle: number = 0): void {
    if (this.cheats) return;
    if (amount > 0 && this.invincible === false) {
      if (this.hyperArmour) {
        amount = Math.floor(amount * 0.66);
      }
      this.hpCurrent -= amount;
      if (this.hpCurrent < 0) {
        this.hpCurrent = 0;
        if (this.canDie) {
          this.dead = true;
        }
      }
      this.updateHitSpark(1, amount);
      this.flashBody(0, 125);
    }
    const force = amount * 0.5;
    const rad = dAngle * (Math.PI / 180);
    this.pushForce.x = force * Math.cos(rad);
    this.pushForce.y = force * Math.sin(rad);
  }

  /** Reports and consumes the semantic trigger the player is standing on. */
  triggerCycle(): TriggerPass | null {
    let matched: TriggerPass | null = null;
    for (const trigger of this.triggerPass) {
      if (
        this.x >= trigger.xx &&
        this.x < trigger.xx + 64 &&
        this.y >= trigger.yy &&
        this.y < trigger.yy + 64
      ) {
        matched = trigger;
      }
    }
    if (matched === null) return null;
    for (let index = this.triggerPass.length - 1; index >= 0; index--) {
      const trigger = this.triggerPass[index];
      const sameEvent =
        matched.encounter !== undefined
          ? trigger.encounter === matched.encounter
          : trigger.encounter === undefined &&
            trigger.dialogue === matched.dialogue;
      if (sameEvent) this.triggerPass.splice(index, 1);
    }
    return matched;
  }

  /** `mode` 0 shows the max-power burst, 1 ticks it, 2 starts its fade. */
  maxPowerX(mode: number = 0): void {
    switch (mode) {
      case 0:
        this.powerX.visible = true;
        this.powerX.alpha = 0.85;
        this.powerXCounter = 0;
        this.powerXScale = 200;
        this.powerXOn = true;
        this.powerXAlpha = 85;
        this.powerXFading = false;
        this.powerX.scaleX = this.powerXScale * 0.005;
        this.powerX.scaleY = this.powerX.scaleX;
        this.flashBody(2, 125);
        break;
      case 1:
        ++this.powerXCounter;
        if (this.powerXCounter < 40) {
          this.powerXScale =
            (this.powerXScale + (800 - this.powerXScale) * 0.25) | 0;
        } else if (this.powerXCounter < 60) {
          this.powerXScale =
            (this.powerXScale + (310 - this.powerXScale) * 0.1) | 0;
        } else {
          this.powerXScale =
            (this.powerXScale + (420 - this.powerXScale) * 0.1) | 0;
          if (this.powerXCounter === 80) {
            this.powerXCounter = 40;
          }
        }
        this.powerX.scaleX = this.powerXScale * 0.005;
        this.powerX.scaleY = this.powerX.scaleX;
        if (this.powerXFading && this.powerXAlpha > 0) {
          this.powerXAlpha -= 5;
          this.powerX.alpha = this.powerXAlpha * 0.01;
          if (this.powerXAlpha === 0) {
            this.powerX.visible = false;
            this.powerXOn = false;
          }
        }
        break;
      case 2:
        this.powerXFading = true;
        break;
    }
  }

  thrownReturn(): [number, number, number, number] {
    return [
      this.swordSizeX,
      this.swordSizeY,
      this.swordLevel,
      this.targetAngle,
    ];
  }

  returnWeapon(): void {
    this.sword.visible = true;
    this.swordBack.visible = true;
    this.swordTrailMain.visible = true;
    this.swordTrailBack.visible = true;
    this.hasWeapon = true;
    this.brightnessSword = 200;
    this.soundAction = PlayerSound.WeaponCaught;
  }

  upgradeArmour(): void {
    this.flashBody(2, 125);
    this.body.sheet = new Bitmap(new SPR_knightArmoured()).bitmapData!;
  }

  /** Eases the body towards its target, unsticking it if two solid frames wedged it in a corner. */
  private moveStep(obsArray: Obstacle[][], barrierArray: Obstacle[]): void {
    let futureX = this.x + (this.xTarget - this.x) * 0.075;
    let futureY = this.y + (this.yTarget - this.y) * 0.075;
    if (this.stuck) {
      futureX = this.x;
      futureY = this.y;
    }
    let moveAllowed = this.collision(futureX, futureY, obsArray, barrierArray);
    if (this.moveAllowedPrevious[0] && this.moveAllowedPrevious[1]) {
      if (moveAllowed[0] === false && moveAllowed[1] === false) {
        while (moveAllowed[0] === false && moveAllowed[1] === false) {
          if (this.xTarget > this.x) {
            futureX--;
          }
          if (this.xTarget < this.x) {
            futureX++;
          }
          if (this.yTarget > this.y) {
            futureY--;
          }
          if (this.yTarget < this.y) {
            futureY++;
          }
          moveAllowed = this.collision(
            futureX,
            futureY,
            obsArray,
            barrierArray,
          );
        }
        this.xTarget = futureX;
        this.yTarget = futureY;
      }
    }
    this.moveAllowedPrevious[0] = moveAllowed[0];
    this.moveAllowedPrevious[1] = moveAllowed[1];
    if (this.animState !== 10) {
      if (moveAllowed[0]) {
        this.x = futureX;
      } else {
        this.xTarget = this.x;
      }
      if (moveAllowed[1]) {
        this.y = futureY;
      } else {
        this.yTarget = this.y;
      }
    }
  }

  private readSpecialKey(input: boolean[]): void {
    if (!input[4]) {
      this.spacePressed = false;
      return;
    }
    // cheats auto-fire: no key debounce, no energy cost and none of the
    // per-weapon locks, so the swing animation is the only thing pacing it
    const ready = this.cheats
      ? this.animState === 0
      : this.spacePressed === false &&
        this.animState < 2 &&
        this.hasWeapon &&
        this.energy > WEAPONS[this.weapon].specialCost &&
        (this.weapon !== 3 || this.blackHoleOn === false);
    if (!ready) return;
    this.spacePressed = true;
    this.specialAttack();
  }

  /** Number keys 1-5; locked weapons are ignored, so the highest unlocked press wins. */
  private readWeaponKeys(input: boolean[]): void {
    if (this.numPressed === false && this.hasWeapon && this.animState === 0) {
      if (input[5] || input[6] || input[7] || input[8] || input[9]) {
        this.numPressed = true;
        let weaponPressed = this.weapon;
        if (input[5]) {
          weaponPressed = 0;
        }
        if (input[6]) {
          weaponPressed = 1;
        }
        for (let slot = 2; slot < 5; slot++) {
          if (input[5 + slot] && this.weaponData[slot] > 0) {
            weaponPressed = slot;
          }
        }
        if (weaponPressed !== this.weapon) {
          this.changeWeapon = weaponPressed;
          this.changeAnimState(10);
        }
      }
    } else if (!input[5] && !input[6] && !input[7] && !input[8] && !input[9]) {
      this.numPressed = false;
    }
  }

  private registerHit(): number {
    this.brightnessSword = 160;
    this.updateGlow();
    return 1;
  }

  private flashBody(type: number, cd: number): void {
    this.colourMaskCD = cd;
    this.colourMaskType = type;
    this.updateGlow();
  }

  private collision(
    futureX: number,
    futureY: number,
    obsArray: Obstacle[][],
    barrierArray: Obstacle[],
  ): [boolean, boolean] {
    return blockMovementAxes(
      this.x,
      this.y,
      futureX,
      futureY,
      this.hitBoxSize,
      this.levelEdgeX,
      this.levelEdgeY,
      obsArray,
      barrierArray,
      this.moveAllowedCurrent,
    );
  }

  private updateHitSpark(mode: number = 0, amount: number = 0): void {
    if (mode === 0) {
      let hitSparkDestroyed = false;
      ++this.hitSparkData.counter;
      this.hitSparkData.alternator = !this.hitSparkData.alternator;
      if (this.hitSparkData.counter >= 5) {
        this.hitSparkData.alphaCurrent -= 3;
        if (this.hitSparkData.alphaCurrent <= 0) {
          hitSparkDestroyed = true;
          this.hitSparkData.alphaCurrent = 0;
        } else {
          this.hitSpark!.alpha = this.hitSparkData.alphaCurrent * 0.01;
          --this.hitSparkData.size;
        }
      } else {
        this.hitSparkData.size += 8;
      }
      if (hitSparkDestroyed) {
        this.removeChild(this.hitSpark!);
        this.hitSpark = null;
      } else {
        let hitSparkScale =
          this.hitSparkData.size * 0.01 + this.hitSparkData.sizeMult;
        if (this.hitSparkData.alternator) {
          hitSparkScale *= 1.45;
        }
        this.hitSpark!.scaleX = hitSparkScale;
        this.hitSpark!.scaleY = hitSparkScale;
        this.hitSpark!.rotation = Math.floor(Math.random() * 360);
      }
    } else {
      if (this.hitSpark != null) {
        this.removeChild(this.hitSpark);
        this.hitSpark = null;
      }
      this.hitSpark = new MovieClip();
      this.addChild(this.hitSpark);
      this.hitSpark.blendMode = "add";
      const hitSparkSpr = new Bitmap(new EFF_playerHit());
      hitSparkSpr.x = hitSparkSpr.width * -0.5;
      hitSparkSpr.y = hitSparkSpr.height * -0.5;
      hitSparkSpr.smoothing = true;
      this.hitSpark.addChild(hitSparkSpr);
      this.hitSparkData = {
        counter: 0,
        alternator: false,
        size: 100,
        alphaCurrent: 100,
        sizeMult: amount * 5 * 0.01,
      };
    }
  }

  private updateInput(dir: number, pressed: boolean): void {
    if (pressed) {
      if (this.isPressed[dir] === false) {
        this.isPressed[dir] = true;
        if (this.dir !== dir) {
          this.dir = dir;
          if (this.animState === 0) {
            this.updateDir();
          }
        }
      }
    } else if (this.isPressed[dir]) {
      this.isPressed[dir] = false;
    }
  }

  private updateDir(): void {
    this.restartTrail = true;
    const facing = FACINGS[this.dir];
    if (this.animState === 0) {
      this.animOffset = facing.animOffset;
    }
    if (facing.flipped !== null) {
      this.flipped = facing.flipped;
    }
    this.stackSprites(facing.swordInFront);
    this.drawTile(this.frame + this.animOffset);
  }

  /** Re-parents the layers into draw order; facing away puts the blade over the body. */
  private stackSprites(swordInFront: boolean): void {
    if (swordInFront) {
      this.addChild(this.swordTrailMain);
      this.addChild(this.sword);
      this.addChild(this.canvasBitmap);
      if (this.hasBack) {
        this.addChild(this.swordTrailBack);
        this.addChild(this.swordBack);
      }
      return;
    }
    if (this.hasBack) {
      this.addChild(this.swordTrailBack);
      this.addChild(this.swordBack);
    }
    this.addChild(this.canvasBitmap);
    this.addChild(this.swordTrailMain);
    this.addChild(this.sword);
  }

  private updateAnim(): void {
    ++this.frameCounter;
    if (
      this.frameCounter !==
      KNIGHT_FRAMES[this.frame + this.animOffset].animSpeed
    ) {
      return;
    }
    this.frameCounter = 0;
    ++this.frame;
    if (this.animState === 10 && this.frame === 5) {
      this.freeze = 24;
    }
    if (
      this.animState === 10 &&
      this.frame === 4 &&
      this.weapon !== this.changeWeapon
    ) {
      this.equipWeapon();
    }
    if (this.frame >= this.frameLimit) {
      if (this.slashing) {
        this.slashing = false;
        this.trailWide = false;
        this.setTrailFrame();
        this.changeAnimState(0);
      }
      if (this.animState === 10) {
        this.changeAnimState(0);
      }
      this.frame = 0;
    }
    this.drawTile(this.frame + this.animOffset);
  }

  /** Mid-swap frame of the weapon-change animation: the new blade appears here. */
  private equipWeapon(): void {
    this.weapon = this.changeWeapon;
    this.guiAction = PlayerGuiAction.WeaponChanged;
    this.action = PlayerAction.WeaponChanged;
    this.flashBody(2, 175);
    this.updateSword();
    const newHasBack = this.weapon === 1 || this.weapon === 3;
    if (newHasBack !== this.hasBack) {
      this.hasBack = newHasBack;
      this.swordBack.visible = newHasBack;
      this.swordTrailBack.visible = false;
    }
    this.trailWide = false;
    this.setTrailFrame();
    this.drawSword();
    this.brightnessSword = 300;
    // the weapon swap deliberately starts above the pulse ceiling
    this.glowPulse.startFalling(35);
    this.updateGlow();
  }

  private setTrailFrame(): void {
    this.swordTrailFrame = this.weapon * 2 + this.swordLevel * 10;
    if (this.hasBack) {
      // only weapons 1 and 3 carry a back blade, and 3's cels follow 1's
      this.swordTrailBackFrame =
        (this.weapon === 3 ? 2 : 0) + this.swordLevel * 4;
    }
    if (this.trailWide) {
      ++this.swordTrailFrame;
      if (this.hasBack) {
        ++this.swordTrailBackFrame;
      }
    }
    this.updateTrail(6);
  }

  private updateSlash(starting: boolean = false): void {
    if (starting) {
      this.frameLimit = 6;
      this.frame = 0;
      this.swordFadeSpeed = 20;
      this.slashing = true;
      this.trailWide = true;
      this.setTrailFrame();
      const pose = SLASH_POSES[this.dir];
      if (pose.flipped !== null) {
        this.flipped = pose.flipped;
      }
      this.slashSpeed = pose.speed;
      this.animOffset = pose.animOffset;
      this.slashAngle = pose.angle;
      this.slashOffX = pose.offX;
      this.slashOffY = pose.offY;
      this.slashXTarget = pose.xTarget;
      this.slashYTarget = pose.yTarget;
      this.drawTile(this.frame + this.animOffset);
    } else if (this.slashing && this.frameCounter >= 0) {
      if (this.frame === 1 && this.animState === 5) {
        this.action = PlayerAction.AxeShockwave;
        this.animState = 99;
        this.soundAction = PlayerSound.AxeShockwave;
      }
      this.slashSpeed += (0 - this.slashSpeed) * 0.33;
      this.slashOffX += (this.slashXTarget - this.slashOffX) * 0.33;
      this.slashOffY += (this.slashYTarget - this.slashOffY) * 0.33;
      const rad = this.slashAngle * (Math.PI / 180);
      const slX = 2 * Math.cos(rad) + this.slashOffX;
      const slY = 2 * Math.sin(rad) + this.slashOffY;
      this.sword.rotation = this.slashAngle;
      this.sword.x = slX;
      this.sword.y = slY;
      if (this.hasBack) {
        this.swordBack.rotation = this.slashAngle + 180;
        this.swordBack.x = slX;
        this.swordBack.y = slY;
      }
      this.slashAngle -= this.slashSpeed;
    }
  }

  /**
   * Ticks the ten trail ghosts. `mode` 0 fades them, 1 snaps them onto the blade,
   * 2/3 hide and show, 4/5 rescale, 6 re-blits the current trail cel.
   */
  private updateTrail(mode: number = 0): void {
    for (
      let trailIndex = 0;
      trailIndex < this.swordTrail.length;
      trailIndex++
    ) {
      const ghost = this.swordTrail[trailIndex];
      const front = trailIndex < 5;
      switch (mode) {
        case 0:
          this.swordTrailData[trailIndex] -= this.swordFadeSpeed;
          ghost.alpha = this.swordTrailData[trailIndex] * 0.01;
          if (this.swordTrailData[trailIndex] <= 0) {
            this.swordTrailData[trailIndex] += 100;
            ghost.rotation = front
              ? this.sword.rotation
              : this.swordBack.rotation;
            ghost.x = this.sword.x;
            ghost.y = this.sword.y;
          }
          break;
        case 1:
          ghost.rotation = front
            ? this.sword.rotation
            : this.swordBack.rotation;
          ghost.x = this.sword.x;
          ghost.y = this.sword.y;
          break;
        case 2:
          ghost.visible = false;
          break;
        case 3:
          ghost.visible = true;
          break;
        case 4:
          ghost.scaleX = this.swordSizeY;
          ghost.scaleY = this.swordSizeX;
          break;
        case 5:
          ghost.scaleY += (0.35 - ghost.scaleY) * 0.075;
          break;
        case 6:
          if (front) {
            this.trailLayer.draw(this.swordTrailFrame | 0);
          } else {
            this.trailBackLayer.draw(this.swordTrailBackFrame | 0);
          }
          break;
      }
    }
  }

  private updateSpin(): void {
    const swordAngle = this.frame * 20 + 121;
    this.sword.rotation = this.frame * 20 + 100;
    const rad = swordAngle * (Math.PI / 180);
    this.sword.x = 21 * Math.cos(rad);
    this.sword.y = 21 * Math.sin(rad) * 0.5 - 5;
    ++this.spinCounter;
    if (this.spinCounter >= 60) {
      this.spinSpeed += 2;
      if (this.spinSpeed === 10) {
        this.invincible = false;
        this.spinOn = false;
      }
      if (this.spinSpeed === 54) {
        this.trailWide = false;
        this.setTrailFrame();
        this.changeAnimState(0);
      }
    } else if (this.spinSpeed > 0) {
      this.spinSpeed -= 5;
    }
    const spinAnimSpeed = Math.floor(this.spinSpeed * 0.1);
    const framePlus = spinAnimSpeed === 0 ? 2 : 1;
    ++this.frameCounter;
    if (this.frameCounter >= spinAnimSpeed) {
      this.frameCounter = 0;
      this.frame += framePlus;
      if (this.frame === 3) {
        this.soundAction = PlayerSound.SwordSpin;
      }
      if (this.frame >= this.frameLimit) {
        this.frame -= this.frameLimit;
      }
      this.drawTile(this.frame + this.animOffset);
    }
  }

  private drawTile(frameIndex: number): void {
    this.scaleX = this.flipped ? 1 : -1;
    const frame = KNIGHT_FRAMES[frameIndex];
    this.canvasBitmap.x = frame.xx - this.canvasBitmap.width * 0.5;
    this.canvasBitmap.y = frame.yy - this.canvasBitmap.height * 0.5;
    if (this.slashing === false && this.animState !== 2) {
      const swordFrame = KNIGHT_SWORD_FRAMES[frameIndex];
      this.sword.x = swordFrame.xx + frame.xx;
      this.sword.y = swordFrame.yy + frame.yy + 2;
      this.sword.rotation = swordFrame.aa;
      if (this.hasBack) {
        this.swordBack.x = this.sword.x;
        this.swordBack.y = this.sword.y;
        this.swordBack.rotation = this.sword.rotation + 180;
      }
    }
    this.body.draw(frameIndex);
    if (this.restartTrail) {
      this.updateTrail(1);
      this.restartTrail = false;
    }
  }

  private drawSword(): void {
    this.swordLayer.draw(this.weapon + this.swordLevel * 5);
    if (this.hasBack) {
      this.swordBackLayer.draw(
        (this.weapon === 3 ? 1 : 0) + this.swordLevel * 2,
      );
    }
  }

  private updateGlow(): void {
    if (this.colourMaskCD > 0) {
      this.colourMaskCD -= 25;
      const c = new Color();
      c.setTint(COLOUR_MASKS[this.colourMaskType], this.colourMaskCD * 0.01);
      this.canvasBitmap.transform.colorTransform = c;
    }
    const glowCounterSword = this.glowPulse.advance();
    const gFinalSword =
      (glowCounterSword * 0.4 + 3) * ((this.swordLevel + 1) * 1.5);
    const glowFilter = new GlowFilter(
      SWORD_COLOURS[this.swordLevel],
      1,
      gFinalSword,
      gFinalSword,
      2 + this.swordLevel * 0.5,
      1,
    );
    this.sword.filters = [glowFilter];
    if (this.hasBack) {
      this.swordBack.filters = [glowFilter];
    }
    if (this.brightnessSword > 0) {
      this.brightnessSword -= 20;
      const bSw = new Color();
      bSw.setTint(16777215, this.brightnessSword * 0.01);
      this.sword.transform.colorTransform = bSw;
      if (this.hasBack) {
        this.swordBack.transform.colorTransform = bSw;
      }
    }
  }

  /** Stored energy stretches the blade and lengthens its reach. */
  private updateSword(): void {
    const stats = WEAPONS[this.weapon];
    this.swordSizeY = 0.75 + this.energy * 0.00027;
    this.swordSizeX = 0.75 + this.energy * stats.scaleMult * 0.001;
    this.sword.scaleX = this.swordSizeY;
    this.sword.scaleY = this.swordSizeX;
    this.updateTrail(4);
    this.swordRadius = stats.range + this.energy * 0.001 * stats.growth;
    if (this.hasBack) {
      this.swordBack.scaleX = this.swordSizeY;
      this.swordBack.scaleY = this.swordSizeX;
    }
  }

  private specialAttack(): void {
    if (this.slashing) {
      this.slashing = false;
      this.trailWide = false;
      this.setTrailFrame();
    }
    switch (this.weapon) {
      case 0:
        this.changeAnimState(4);
        this.action = PlayerAction.DashStarted;
        break;
      case 1:
        this.changeAnimState(3);
        this.action = PlayerAction.ThrowWeapon;
        break;
      case 2:
        this.changeAnimState(5);
        break;
      case 3:
        this.animState = 99;
        this.changeAnimState(1);
        this.blackHoleOn = true;
        this.action = PlayerAction.BlackHole;
        break;
      case 4:
        this.changeAnimState(2);
        break;
    }
    if (!this.cheats) {
      this.updateEnergy(WEAPONS[this.weapon].specialCost * -1);
    }
    this.flashBody(2, 175);
  }
}
